/**
 * 对话工作流会话：列表 / 保存 / 读取（后端表 chat_workflow_sessions）
 * 与 VideoCopy 等项目接口一致，走 axios 实例与 Authorization token。
 */

import api from '@/utils/api'

/**
 * 兼容多种后端/Dingo 包装：data.list、data 为数组、双层 data、result、items 等
 */
function unwrapList(res) {
  if (res == null) return []
  if (typeof res === 'string') {
    try {
      const j = JSON.parse(res)
      return unwrapList(j)
    } catch {
      return []
    }
  }
  if (!res || typeof res !== 'object') return []
  if (Array.isArray(res)) return res
  if (Array.isArray(res.list)) return res.list
  if (Array.isArray(res.result)) return res.result
  if (Array.isArray(res.records)) return res.records
  if (Array.isArray(res.items)) return res.items

  const d = res.data
  if (Array.isArray(d)) return d
  if (d && typeof d === 'object') {
    if (Array.isArray(d.list)) return d.list
    if (Array.isArray(d.records)) return d.records
    if (Array.isArray(d.items)) return d.items
    if (Array.isArray(d.data)) return d.data
    const inner = d.data
    if (inner && typeof inner === 'object' && Array.isArray(inner.list)) return inner.list
  }
  return []
}

/** 业务 code 非成功时抛错，避免静默空列表 */
function assertApiOk(res) {
  if (res == null || typeof res !== 'object') return
  const c = res.code
  if (c === undefined || c === null) return
  const n = Number(c)
  if (n === 0 || n === 200) return
  const msg = res.message || res.msg || res.error || `接口 code=${c}`
  throw new Error(msg)
}

/**
 * @param {{ limit?: number }} [opts]
 * @returns {Promise<Array<{ uuid: string, title: string, thumb_url?: string, updated_at?: string, created_at?: string }>>}
 */
export async function fetchChatWorkflowSessionList(opts) {
  const limit = opts && opts.limit != null ? opts.limit : 20
  const res = await api.get('/chat_workflow_sessions', { params: { limit } })
  assertApiOk(res)
  const raw = unwrapList(res)
  return raw.map((row, idx) => ({
    id: String(row.uuid ?? row.id ?? idx),
    uuid: String(row.uuid ?? row.id ?? ''),
    title: row.title ?? row.name ?? '未命名',
    thumb: row.thumb_url ?? row.thumb ?? row.cover ?? '',
    time: formatListTime(row.updated_at ?? row.updatedAt ?? row.created_at ?? row.createdAt ?? '')
  }))
}

/**
 * @param {string} uuid
 */
export async function fetchChatWorkflowSessionDetail(uuid, opts) {
  const id = String(uuid || '').trim()
  if (!id) throw new Error('缺少 session')
  const tail = opts && opts.tail != null ? Number(opts.tail) : 20
  const stripDataImages =
    opts && opts.strip_data_images != null ? opts.strip_data_images : 1
  const res = await api.get(`/chat_workflow_sessions/${encodeURIComponent(id)}`, {
    params: {
      tail: Number.isFinite(tail) ? tail : 20,
      include_state_json: 0,
      strip_data_images: stripDataImages ? 1 : 0
    }
  })
  assertApiOk(res)
  let data = res && res.data != null ? res.data : res
  if (data && typeof data === 'object' && data.data && (data.data.uuid != null || data.data.state_json != null)) {
    data = data.data
  }
  if (!data || (!data.uuid && !data.state_json && data.state == null)) {
    throw new Error('会话不存在或无权访问')
  }
  let state = data.state
  if (state == null && data.state_json != null) {
    try {
      state = typeof data.state_json === 'string' ? JSON.parse(data.state_json) : data.state_json
    } catch {
      state = null
    }
  }
  return {
    uuid: data.uuid || id,
    title: data.title || '',
    thumb_url: data.thumb_url || '',
    state: state && typeof state === 'object' ? state : {},
    meta: data.meta && typeof data.meta === 'object' ? data.meta : {}
  }
}

/**
 * @param {{ uuid: string, title?: string, thumb_url?: string, state: object }} payload
 */
export async function saveChatWorkflowSession(payload) {
  const uuid = String(payload.uuid || '').trim()
  if (!uuid) throw new Error('缺少 uuid')
  const res = await api.post('/chat_workflow_sessions', {
    uuid,
    title: payload.title || '未命名',
    thumb_url: payload.thumb_url || '',
    state: payload.state
  })
  assertApiOk(res)
}

function formatListTime(raw) {
  if (raw == null || raw === '') return ''
  const s = String(raw)
  if (s.length >= 16 && s.indexOf('T') === -1) return s.slice(0, 16).replace('T', ' ')
  try {
    const d = new Date(s)
    if (Number.isNaN(d.getTime())) return s
    const y = d.getFullYear()
    const m = String(d.getMonth() + 1).padStart(2, '0')
    const day = String(d.getDate()).padStart(2, '0')
    const h = String(d.getHours()).padStart(2, '0')
    const min = String(d.getMinutes()).padStart(2, '0')
    return `${y}-${m}-${day} ${h}:${min}`
  } catch {
    return s
  }
}
